import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "../ui/Icon";
import { MenuPortal } from "./MenuPortal";
import { useMenuPosition } from "./useMenuPosition";
import { useNotificationsFeed } from "../../hooks/useNotificationsFeed";
import { useTransferEventStream } from "../../hooks/useTransferEventStream";
import { useMe } from "../../hooks/useMe";
import { isAdminUser } from "../../lib/permissions";
import { NotificationsInboxSummary } from "../notifications/NotificationsInboxSummary";

export const NotificationsMenu = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuStyle = useMenuPosition(anchorRef, open, { align: "right" });
  const { notifications, loading, error, unreadCount, refresh, refreshUnreadCount, markRead, dismiss } = useNotificationsFeed();
  const { user, appConfig } = useMe();
  const interInstanceTransferEnabled = appConfig?.features?.interInstanceTransferEnabled ?? false;

  const hasUnread = unreadCount > 0;
  const unreadNotifications = useMemo(() => notifications.filter((n) => !n.read), [notifications]);

  // Live bell: a per-admin `notification` SSE event refreshes the unread count
  // (and the feed if the panel is open) immediately, ahead of the 15s poll.
  const openRef = useRef(open);
  openRef.current = open;
  const handleNotificationEvent = useCallback(() => {
    void refreshUnreadCount();
    if (openRef.current) void refresh();
  }, [refreshUnreadCount, refresh]);

  // The transfer SSE endpoint is admin- AND feature-gated; opening it on an
  // install where inter-instance transfer is off returns a permanent 403.
  useTransferEventStream({
    enabled: isAdminUser(user) && interInstanceTransferEnabled,
    onNotification: handleNotificationEvent,
  });

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleMarkAllRead = async () => {
    await markRead();
  };

  const preloadedRef = useRef(false);
  useEffect(() => {
    // Preload unread count so the badge reflects state before opening.
    if (preloadedRef.current) return;
    preloadedRef.current = true;
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  const handleItemClick = (id: number, href?: string | null) => {
    setOpen(false);
    if (href) {
      if (href.startsWith("http://") || href.startsWith("https://")) {
        window.location.href = href;
        return;
      }
      navigate(href);
      return;
    }
    navigate("/profile?tab=notification-center");
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        ref={anchorRef}
        aria-label="Alerts"
        className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/70 px-1.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 xl:px-3 xl:py-2"
      >
        <Icon icon="lucide:bell" className="h-4 w-4" />
        <span className="hidden xl:inline">Alerts</span>
        {hasUnread && <span className="h-2 w-2 rounded-full bg-primary" aria-label="Unread notifications" />}
      </button>

      {open && (
        <MenuPortal>
          <div
            ref={menuRef}
            style={menuStyle}
            className="z-[100] w-[22rem] max-w-[calc(100vw-1.5rem)] sm:w-[24rem] rounded-lg border border-border/50 bg-card/95 p-4 text-xs text-muted-foreground shadow-soft"
          >
            <div className="flex items-center justify-between gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <span>Notification Center</span>
              <div className="flex items-center gap-2 text-[11px] normal-case">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => void refresh()}
                >
                  Refresh
                </button>
                {hasUnread && (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => void handleMarkAllRead()}
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 max-h-96 overflow-y-auto qb-scroll pr-1">
              <NotificationsInboxSummary
                items={unreadNotifications}
                loading={loading}
                error={error}
                maxItems={8}
                compact
                onRefresh={refresh}
                onMarkRead={markRead}
                onDismiss={dismiss}
                onItemClick={(item) => handleItemClick(item.id, item.actionHref)}
              />
            </div>
            <div className="mt-3 flex items-center justify-end text-xs text-primary">
              <button
                type="button"
                className="underline underline-offset-2"
                onClick={() => {
                  setOpen(false);
                  navigate("/profile?tab=notification-center");
                }}
              >
                View Notification Center
              </button>
            </div>
          </div>
        </MenuPortal>
      )}
    </div>
  );
};
